import {
  collection,
  doc,
  getDoc,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import firestoreInstance from "../services/firestoreInstance";
import type { PublicInscritRow, PublicInscritsListResponse } from "../types";

const BATCH = 40;

type TableauDoc = {
  id: string;
  name?: string;
  label?: string;
  date?: Timestamp | Date | null;
  realNbPlaces?: number;
  nbPlace?: number;
};

function boardDisplayTitle(t: TableauDoc): string {
  const name = (t.name ?? "").trim();
  const label = (t.label ?? "").trim();
  if (name && label) return `${name} — ${label}`;
  return name || label || "Tableau";
}

function formatBoardDate(t: TableauDoc): string | null {
  const raw = t.date;
  if (!raw) return null;
  try {
    const dt =
      raw instanceof Timestamp
        ? raw.toDate()
        : raw instanceof Date
          ? raw
          : (raw as { toDate?: () => Date }).toDate?.() ?? null;
    if (!dt || Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function toIso(d: unknown): string | null {
  if (!d) return null;
  if (d instanceof Timestamp) return d.toDate().toISOString();
  if (d instanceof Date) return d.toISOString();
  if (typeof (d as Timestamp).toDate === "function") {
    return (d as Timestamp).toDate().toISOString();
  }
  return null;
}

/** Clé numérique pour tri décroissant des points (non numériques / vides en dernier). */
function pointsSortKey(raw: string): number {
  const trimmed = raw.trim();
  if (!trimmed) return Number.NEGATIVE_INFINITY;

  let compact = trimmed.replace(/\s/g, "");
  if (/pts\.?$/i.test(compact)) {
    compact = compact.replace(/pts\.?$/i, "");
  }
  if (/^-?\d+([.,]\d+)?$/.test(compact)) {
    const normalized = compact.replace(",", ".");
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
  }

  const m = trimmed.match(/-?\d+(?:[.,]\d+)?/);
  if (m) {
    const normalized = m[0].replace(/\s/g, "").replace(",", ".");
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
  }
  return Number.NEGATIVE_INFINITY;
}

/**
 * Charge la liste des inscrits pour le tournoi courant (utilisateur connecté).
 * N’accède pas à `participation/.../private` — pas d’e-mail ni de statut de paiement.
 * L’agrégation et le tri sont faits côté client.
 */
export async function fetchInscritsListClient(
  tournamentId: string
): Promise<PublicInscritsListResponse> {
  const db = firestoreInstance;

  const [partSnap, tabSnap] = await Promise.all([
    getDocs(collection(db, "tournois", tournamentId, "participation")),
    getDocs(collection(db, "tournois", tournamentId, "tableaux")),
  ]);

  const tableaux: TableauDoc[] = tabSnap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<TableauDoc, "id">),
  }));

  const totalPlaces = tableaux.reduce((sum, t) => {
    const cap = Number(t.realNbPlaces ?? t.nbPlace ?? 0);
    return sum + (Number.isFinite(cap) && cap > 0 ? cap : 0);
  }, 0);

  const playerIds = Array.from(
    new Set(
      partSnap.docs
        .map((d) => d.data().playersId as string | undefined)
        .filter((id): id is string => Boolean(id))
    )
  );

  const playersMap = new Map<string, Record<string, unknown>>();
  for (let i = 0; i < playerIds.length; i += BATCH) {
    const batch = playerIds.slice(i, i + BATCH);
    const snaps = await Promise.all(
      batch.map((id) => getDoc(doc(db, "players", id)))
    );
    snaps.forEach((s) => {
      if (s.exists()) {
        playersMap.set(s.id, s.data() as Record<string, unknown>);
      }
    });
  }

  const rows: PublicInscritRow[] = [];

  for (const docSnap of partSnap.docs) {
    const data = docSnap.data();
    const tableauId = data.tableauId as string | undefined;
    const playersId = data.playersId as string | undefined;
    if (!tableauId || !playersId) continue;

    const tableau = tableaux.find((t) => t.id === tableauId);
    if (!tableau) continue;

    const pl = playersMap.get(playersId) ?? {};
    const name = typeof pl.name === "string" ? pl.name.trim() : "";
    const surname = typeof pl.surname === "string" ? pl.surname.trim() : "";
    const playerLine = [name, surname].filter(Boolean).join(" ").trim() || "—";
    const club = typeof pl.club === "string" ? pl.club.trim() : "";
    const categorie =
      typeof pl.categorie === "string" ? pl.categorie.trim() : "";
    const points =
      typeof pl.points === "string"
        ? pl.points.trim()
        : pl.points != null
          ? String(pl.points).trim()
          : "";

    rows.push({
      boardTitle: boardDisplayTitle(tableau),
      boardDateLabel: formatBoardDate(tableau),
      playerLine,
      club,
      categorie,
      points,
      registeredAt: toIso(data.dateInscription),
    });
  }

  rows.sort((a, b) => {
    const bt = a.boardTitle.localeCompare(b.boardTitle, "fr");
    if (bt !== 0) return bt;
    const pa = pointsSortKey(a.points);
    const pb = pointsSortKey(b.points);
    if (pb !== pa) return pb - pa;
    const pn = a.playerLine.localeCompare(b.playerLine, "fr");
    if (pn !== 0) return pn;
    return (b.registeredAt ?? "").localeCompare(a.registeredAt ?? "");
  });

  return {
    rows,
    totalPlaces,
    totalInscrits: rows.length,
  };
}
